/**
 * EffectsLayer — fully procedural, snapshot-diff-driven combat FX layer.
 *
 * Subscribes to MatchConnection.onSnapshot exactly like Sfx does so it stays
 * 100% presentational (Rule 1: NEVER writes back to MatchState). On each
 * snapshot we diff the previous and current state to detect "events":
 *
 *   • projectile_fire   — muzzle flash + shell casing + camera kick at fire
 *                         origin (squad/turret/neutral). Briefly lights the
 *                         floor under the shooter.
 *   • projectile_hit    — directional spark spray + flat white impact pop
 *                         + smoke puff at the projectile's last-known pos.
 *   • squad_death       — directional blood/energy splatter, debris flecks,
 *                         persistent ichor decal stamped on the floor.
 *   • building_destroyed — multi-stage explosion (white-hot core, expanding
 *                         shockwave ring, dust burst), persistent scorch
 *                         decal, heavy camera shake.
 *   • core_damage       — when the VIEWER's core HP drops, trigger the
 *                         damage vignette flash + medium shake.
 *
 * All particles are pooled Phaser.Image instances drawn from the procedural
 * 'fx' / 'polish' atlases. Active particles live in a single array updated
 * once per frame from EffectsLayer.update(nowMs); finished particles are
 * recycled. Persistent decals (scorch / blood) live in a separate FIFO with
 * a hard cap so a long match doesn't leak GPU objects.
 *
 * The "shake" callback is dependency-injected (CameraShake) so this layer
 * stays pure rendering. Audio remains owned by Sfx — these two layers run
 * side-by-side off the same MatchConnection.
 */

import type {
  EntityId,
  MatchConnection,
  MatchState,
  PlayerId,
  Projectile,
  Squad,
  Unsubscribe,
} from '@signalclash/shared';
import Phaser from 'phaser';

// ---------------------------------------------------------------------------
// Tuning constants — tweak these to dial up/down the "punch" of the game.
// ---------------------------------------------------------------------------

// Particle lifetimes (ms). Combat FX must be SHORT so lingering smoke doesn't
// obscure line of sight from the top-down camera (see prompt §1).
const MUZZLE_FLASH_MS      = 80;     // 2-3 frames at 60 Hz
const MUZZLE_SMOKE_MS      = 420;    // muzzle smoke puff
const HIT_FLASH_MS         = 120;
const HIT_SPARK_MS         = 300;
const SMOKE_PUFF_MS        = 420;
const EXPLOSION_CORE_MS    = 300;
const EXPLOSION_RING_MS    = 500;
const EXPLOSION_CORE2_MS   = 420;    // delayed secondary fireball
// V1.8 facelift — shell casings were lingering 4 full seconds at full size,
// piling up dozens of brass blobs each the size of half a squad. Now: shorter
// flight, much shorter rest, and (in spawnShellCasing below) shrunk scale.
const SHELL_CASING_MS      = 600;    // brass arcs out, then settles
const SHELL_RESTED_MS      = 1200;   // brass lingers on the floor before fading

// Decal caps (FIFO recycle once we exceed). Keep modest so memory stays low.
const MAX_SCORCH_DECALS  = 24;
const MAX_BLOOD_DECALS   = 60;

// Atlas keys.
const TEX_FX     = 'fx';
const TEX_POLISH = 'polish';

// ---------------------------------------------------------------------------
// Camera-shake handle. Optional dep so unit tests can leave it null.
// ---------------------------------------------------------------------------

export interface ShakeHandle {
  micro(): void;       // subtle single-shot feedback
  light(): void;       // sustained fire recoil
  medium(): void;      // hit / death
  heavy(): void;       // building destroyed / core damaged
  // Climactic punch — pan + brief zoom + heavy shake centred on a world
  // pos. Implementations may no-op punch if they only support shake.
  punch?(focusX: number, focusY: number, zoomMul?: number, durMs?: number): void;
}

// ---------------------------------------------------------------------------
// Particle record. One per active Image; pooled into `pool` when finished.
// ---------------------------------------------------------------------------

interface Particle {
  img: Phaser.GameObjects.Image;
  bornMs: number;
  durMs: number;
  // Position + linear velocity in world space (px / s).
  x: number; y: number;
  vx: number; vy: number;
  // Optional spin (deg/s) and scale lerp.
  rotSpeedDeg: number;
  scaleFrom: number; scaleTo: number;
  alphaFrom: number; alphaTo: number;
  // Drag (per second). 0 = constant velocity; 1 = halve per second; high = quick stop.
  drag: number;
  // If true the particle holds at its end-state for SHELL_RESTED_MS before
  // fading to 0 — used for shell casings settling on the floor.
  restMs: number;
}

// ---------------------------------------------------------------------------
// EffectsLayer
// ---------------------------------------------------------------------------

export class EffectsLayer {
  private prev: MatchState | null = null;
  private unsub: Unsubscribe | null = null;
  private active: Particle[] = [];
  private pool: Phaser.GameObjects.Image[] = [];
  // Persistent decals (scorch, blood) tracked as FIFOs so we can evict the
  // oldest when we exceed the per-kind cap. Each decal is a long-lived Image.
  private scorchDecals: Phaser.GameObjects.Image[] = [];
  private bloodDecals: Phaser.GameObjects.Image[] = [];
  private viewerId: PlayerId;
  private prevViewerCoreHp = new Map<number, number>();

  constructor(
    private readonly scene: Phaser.Scene,
    viewerId: PlayerId,
    private readonly shake: ShakeHandle | null = null,
  ) {
    this.viewerId = viewerId;
  }

  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
    this.prevViewerCoreHp.clear();
  }

  attach(connection: MatchConnection): void {
    this.detach();
    this.unsub = connection.onSnapshot((snap) => this.onSnapshot(snap));
  }

  detach(): void {
    this.unsub?.();
    this.unsub = null;
    this.prev = null;
  }

  dispose(): void {
    this.detach();
    for (const p of this.active) p.img.destroy();
    for (const img of this.pool) img.destroy();
    for (const img of this.scorchDecals) img.destroy();
    for (const img of this.bloodDecals) img.destroy();
    this.active.length = 0;
    this.pool.length = 0;
    this.scorchDecals.length = 0;
    this.bloodDecals.length = 0;
  }

  /** Per-frame update — call from scene.update() / ClientLoop with performance.now(). */
  update(nowMs: number): void {
    if (this.active.length === 0) return;
    // Iterate backwards so splice() is safe.
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      if (p === undefined) continue;
      const age = nowMs - p.bornMs;
      const totalDur = p.durMs + p.restMs;
      if (age >= totalDur) {
        this.recycle(p);
        this.active.splice(i, 1);
        continue;
      }
      // Velocity + drag (per-second drag using ~16ms step approximation; the
      // exact dt isn't critical for short-lived FX).
      const dtSec = 1 / 60;
      if (p.drag > 0) {
        const k = Math.exp(-p.drag * dtSec);
        p.vx *= k;
        p.vy *= k;
      }
      p.x += p.vx * dtSec;
      p.y += p.vy * dtSec;
      p.img.setPosition(p.x, p.y);
      // Phase 1 — animated (age < durMs): interpolate scale + alpha + rotation.
      // Phase 2 — rest (age >= durMs): hold scale/alpha for restMs then fade out.
      if (age <= p.durMs) {
        const t = age / p.durMs;
        const scale = p.scaleFrom + (p.scaleTo - p.scaleFrom) * t;
        const alpha = p.alphaFrom + (p.alphaTo - p.alphaFrom) * t;
        p.img.setScale(scale);
        p.img.setAlpha(alpha);
        if (p.rotSpeedDeg !== 0) p.img.setAngle(p.img.angle + p.rotSpeedDeg * dtSec);
      } else {
        // Linear fade out across the rest window.
        const fadeT = (age - p.durMs) / Math.max(1, p.restMs);
        p.img.setAlpha(p.alphaTo * (1 - fadeT));
      }
    }
  }

  // -------------------------------------------------------------------------
  // Snapshot diffing
  // -------------------------------------------------------------------------

  private onSnapshot(snap: MatchState): void {
    if (this.prev === null) {
      this.prev = snap;
      // Seed viewer-core HP map so the first delta isn't bogus.
      for (const core of Object.values(snap.cores)) {
        if (core.ownerId === this.viewerId) this.prevViewerCoreHp.set(core.id, core.hp);
      }
      return;
    }
    this.diffProjectiles(this.prev, snap);
    this.diffSquads(this.prev, snap);
    this.diffBuildings(this.prev, snap);
    this.diffCores(this.prev, snap);
    this.diffControlNodes(this.prev, snap);
    this.prev = snap;
  }

  private diffProjectiles(prev: MatchState, next: MatchState): void {
    const nowMs = performance.now();
    // Fires: new projectile in next ⇒ muzzle flash at fire origin.
    for (const [idStr, proj] of Object.entries(next.projectiles)) {
      const id = idStr as unknown as EntityId;
      if (prev.projectiles[id] !== undefined) continue;
      const origin = this.resolveFireOrigin(next, proj);
      if (origin === null) continue;
      const angleRad = Math.atan2(proj.velocity.y, proj.velocity.x);
      this.spawnMuzzleFlash(origin.x, origin.y, angleRad, nowMs);
      this.spawnShellCasing(origin.x, origin.y, angleRad, nowMs);
      // Per-shot camera feedback: micro for friendly fire (distant enemies
      // would cause camera spam), light for hits landing on our squads.
      if (proj.ownerId === this.viewerId) this.shake?.micro();
    }
    // Hits: projectile gone in next ⇒ impact at the last-known position. Use
    // the previous snapshot pos since `next` no longer has the entity.
    for (const [idStr, proj] of Object.entries(prev.projectiles)) {
      const id = idStr as unknown as EntityId;
      if (next.projectiles[id] !== undefined) continue;
      const angleRad = Math.atan2(proj.velocity.y, proj.velocity.x);
      this.spawnImpact(proj.pos.x, proj.pos.y, angleRad, nowMs);
      if (proj.ownerId !== this.viewerId && proj.target.kind === 'squad') {
        // Enemy bullet landing on (presumably) one of our squads — let it punch.
        this.shake?.light();
      }
    }
  }

  private diffSquads(prev: MatchState, next: MatchState): void {
    const nowMs = performance.now();
    for (const [idStr, dead] of Object.entries(prev.squads)) {
      const id = idStr as unknown as EntityId;
      if (next.squads[id] !== undefined) continue;
      this.spawnSquadDeath(dead, nowMs);
      if (dead.ownerId === this.viewerId) this.shake?.medium();
    }
  }

  private diffBuildings(prev: MatchState, next: MatchState): void {
    const nowMs = performance.now();
    for (const [idStr, b] of Object.entries(prev.buildings)) {
      const id = idStr as unknown as EntityId;
      if (next.buildings[id] !== undefined) continue;
      this.spawnExplosion(b.pos.x, b.pos.y, nowMs, b.kind === 'production_hub' ? 1.6 : 1.0);
      this.shake?.heavy();
    }
  }

  // V1.5+ — flip shockwave. When a control node's ownerId changes between
  // snapshots, fire an expanding EMP-style ring tinted with the NEW owner's
  // color, plus a radial spark burst. This is the §16 "felt on the
  // battlefield" promise: a capture should feel like a victory, not a status
  // bar filling up. The defender also gets a micro-shake on top of the
  // existing CriticalFlipBanner so the kinetic feedback layers.
  private diffControlNodes(prev: MatchState, next: MatchState): void {
    const nowMs = performance.now();
    for (const [idStr, nextNode] of Object.entries(next.controlNodes)) {
      const id = idStr as unknown as EntityId;
      const prevNode = prev.controlNodes[id];
      if (prevNode === undefined) continue;
      if (prevNode.ownerId === nextNode.ownerId) continue;
      const tint = nextNode.ownerId === 0
        ? 0x66d4ff
        : nextNode.ownerId === 1
          ? 0xff9050
          : 0xbcc4cc;  // neutral flip — also worth a subdued ring
      this.spawnFlipShockwave(nextNode.pos.x, nextNode.pos.y, tint, nowMs);
      // Shake intensity depends on whose relay is changing. Losing a relay
      // hits harder than gaining one — kinetic asymmetry mirrors the
      // strategic asymmetry.
      if (prevNode.ownerId === this.viewerId) this.shake?.medium();
      else if (nextNode.ownerId === this.viewerId) this.shake?.light();
      else this.shake?.micro();
    }
  }

  private diffCores(prev: MatchState, next: MatchState): void {
    const nowMs = performance.now();
    for (const [idStr, core] of Object.entries(next.cores)) {
      const id = idStr as unknown as EntityId;
      const before = prev.cores[id];
      if (before === undefined) continue;
      // Core destruction — HP crossed from positive to zero. Fire the
      // climactic sequence: triple-chained explosion + camera punch
      // toward the dying Core. Both players see it; loser/winner
      // distinction is handled by the EOM modal that follows.
      if (before.hp > 0 && core.hp <= 0) {
        this.spawnCoreDestructionSequence(core.pos.x, core.pos.y, nowMs);
        // Punch toward the dying Core. Slightly stronger zoom for the
        // viewer-loss case so the moment of defeat lands hardest.
        const zoomMul = core.ownerId === this.viewerId ? 1.32 : 1.22;
        if (this.shake?.punch !== undefined) {
          this.shake.punch(core.pos.x, core.pos.y, zoomMul, 900);
        } else {
          this.shake?.heavy();
        }
        continue;
      }
      if (core.ownerId !== this.viewerId) continue;
      const lastHp = this.prevViewerCoreHp.get(core.id) ?? core.hp;
      if (core.hp < lastHp) {
        const lost = lastHp - core.hp;
        if (lost >= 5) this.shake?.heavy();
        else this.shake?.medium();
      }
      this.prevViewerCoreHp.set(core.id, core.hp);
    }
  }

  // Three-stage explosion timed across ~1.4 s so the Core feels like a
  // structure failing, not a single boom. Each stage offsets a fresh
  // explosion at a slightly randomised position around the Core and
  // adds extra debris sparks. The total visible duration aligns with
  // the camera punch (700 ms) and the announcer / EOM delay.
  private spawnCoreDestructionSequence(x: number, y: number, nowMs: number): void {
    this.spawnExplosion(x, y, nowMs, 2.4);
    // Secondary at +220 ms — slight offset for chaotic feel.
    setTimeout(() => {
      const ox = x + (Math.random() - 0.5) * 50;
      const oy = y + (Math.random() - 0.5) * 30;
      this.spawnExplosion(ox, oy, performance.now(), 1.9);
    }, 220);
    // Final at +520 ms — biggest of the three, slightly above center
    // so debris reads as upward eruption.
    setTimeout(() => {
      const ox = x + (Math.random() - 0.5) * 30;
      const oy = y - 16 + (Math.random() - 0.5) * 20;
      this.spawnExplosion(ox, oy, performance.now(), 2.8);
    }, 520);
  }

  // -------------------------------------------------------------------------
  // Helpers — resolve a projectile's fire origin from sim references.
  // -------------------------------------------------------------------------

  private resolveFireOrigin(next: MatchState, proj: Projectile): { x: number; y: number } | null {
    if (proj.fromSquadId !== null) {
      const s = next.squads[proj.fromSquadId];
      if (s !== undefined) return { x: s.pos.x, y: s.pos.y };
    }
    if (proj.fromBuildingId !== null) {
      const b = next.buildings[proj.fromBuildingId];
      if (b !== undefined) return { x: b.pos.x, y: b.pos.y };
    }
    if (proj.fromNeutralUnitId !== null) {
      // Neutral units may be removed from state by the time we process; fall
      // back to the projectile's own current position (close enough).
      return { x: proj.pos.x, y: proj.pos.y };
    }
    return { x: proj.pos.x, y: proj.pos.y };
  }

  // -------------------------------------------------------------------------
  // Particle spawn helpers
  // -------------------------------------------------------------------------

  private spawnMuzzleFlash(x: number, y: number, angleRad: number, nowMs: number): void {
    if (!this.scene.textures.exists(TEX_FX)) return;
    // Position the flash a few px in front of the shooter so it visually
    // emerges from the muzzle rather than the body center.
    const ox = x + Math.cos(angleRad) * 16;
    const oy = y + Math.sin(angleRad) * 16;
    // Primary muzzle star-burst (larger, brighter)
    this.spawn({
      tex: TEX_FX, frame: 'muzzle_flash',
      x: ox, y: oy, vx: 0, vy: 0,
      durMs: MUZZLE_FLASH_MS, restMs: 0,
      scaleFrom: 1.0, scaleTo: 1.45,
      alphaFrom: 1, alphaTo: 0,
      angleDeg: angleRad * (180 / Math.PI),
      depth: 13.8, blend: Phaser.BlendModes.ADD,
      tint: 0xfff0c0,
      drag: 0, rotSpeedDeg: 0,
      now: nowMs,
    });
    // Secondary smaller star at a slightly random angle for variance
    this.spawn({
      tex: TEX_FX, frame: 'muzzle_flash',
      x: ox + Math.cos(angleRad) * 4, y: oy + Math.sin(angleRad) * 4, vx: 0, vy: 0,
      durMs: MUZZLE_FLASH_MS * 0.65, restMs: 0,
      scaleFrom: 0.5, scaleTo: 0.85,
      alphaFrom: 0.8, alphaTo: 0,
      angleDeg: angleRad * (180 / Math.PI) + (Math.random() - 0.5) * 30,
      depth: 13.6, blend: Phaser.BlendModes.ADD,
      tint: 0xffffff,
      drag: 0, rotSpeedDeg: 0,
      now: nowMs,
    });
    // Smoke puff drifting forward
    this.spawn({
      tex: TEX_FX, frame: 'smoke_puff',
      x: ox, y: oy,
      vx: Math.cos(angleRad) * 50, vy: Math.sin(angleRad) * 50,
      durMs: MUZZLE_SMOKE_MS, restMs: 0,
      scaleFrom: 0.35, scaleTo: 1.05,
      alphaFrom: 0.6, alphaTo: 0,
      angleDeg: 0,
      depth: 12.5, blend: Phaser.BlendModes.NORMAL,
      tint: 0xb0b8c0,
      drag: 1.5, rotSpeedDeg: 35,
      now: nowMs,
    });
  }

  private spawnShellCasing(x: number, y: number, angleRad: number, nowMs: number): void {
    if (!this.scene.textures.exists(TEX_FX)) return;
    // Eject perpendicular to the shooter's facing with some randomness.
    const side = Math.random() < 0.5 ? -1 : 1;
    const perpRad = angleRad + side * (Math.PI / 2);
    const speed = 90 + Math.random() * 50;
    const ox = x + Math.cos(angleRad) * 6;
    const oy = y + Math.sin(angleRad) * 6;
    this.spawn({
      tex: TEX_FX, frame: 'shell_casing',
      x: ox, y: oy,
      vx: Math.cos(perpRad) * speed, vy: Math.sin(perpRad) * speed,
      durMs: SHELL_CASING_MS, restMs: SHELL_RESTED_MS,
      // V1.8 facelift — scale drastically reduced (1.0→0.28 / 0.85→0.20).
      // Previous values combined with the FX atlas's 192-px frame made each
      // casing render ~30 world units long (≈ half a squad). New scale puts
      // them at ~8 world units — proper brass shells, not floating debris.
      scaleFrom: 0.28, scaleTo: 0.20,
      alphaFrom: 1, alphaTo: 0.7,
      angleDeg: Math.random() * 360,
      depth: 6.2, blend: Phaser.BlendModes.NORMAL,
      tint: 0xffffff,
      drag: 5.5, rotSpeedDeg: side * (340 + Math.random() * 240),
      now: nowMs,
    });
  }

  private spawnImpact(x: number, y: number, angleRad: number, nowMs: number): void {
    if (!this.scene.textures.exists(TEX_FX)) return;
    // Bright hit flash
    this.spawn({
      tex: TEX_FX, frame: 'hit_small',
      x, y, vx: 0, vy: 0,
      durMs: HIT_FLASH_MS, restMs: 0,
      scaleFrom: 0.8, scaleTo: 1.4,
      alphaFrom: 1, alphaTo: 0,
      angleDeg: Math.random() * 360,
      depth: 13.5, blend: Phaser.BlendModes.ADD,
      tint: 0xffffff,
      drag: 0, rotSpeedDeg: 0,
      now: nowMs,
    });
    // Directional spark spray — 5 sparks fanned out OPPOSITE to bullet travel
    // (sparks come back toward the shooter on an impact, like a ricochet).
    const back = angleRad + Math.PI;
    for (let i = 0; i < 5; i++) {
      const spread = (Math.random() - 0.5) * 1.4;
      const a = back + spread;
      const speed = 140 + Math.random() * 120;
      this.spawn({
        tex: TEX_FX, frame: 'spark',
        x, y,
        vx: Math.cos(a) * speed, vy: Math.sin(a) * speed,
        durMs: HIT_SPARK_MS, restMs: 0,
        scaleFrom: 0.9, scaleTo: 0.3,
        alphaFrom: 1, alphaTo: 0,
        angleDeg: 0,
        depth: 13, blend: Phaser.BlendModes.ADD,
        tint: 0xfff0c0,
        drag: 3, rotSpeedDeg: 0,
        now: nowMs,
      });
    }
    // Soft smoke puff
    this.spawn({
      tex: TEX_FX, frame: 'smoke_puff',
      x, y,
      vx: Math.cos(back) * 25, vy: Math.sin(back) * 25,
      durMs: SMOKE_PUFF_MS, restMs: 0,
      scaleFrom: 0.35, scaleTo: 0.9,
      alphaFrom: 0.55, alphaTo: 0,
      angleDeg: 0,
      depth: 12.5, blend: Phaser.BlendModes.NORMAL,
      tint: 0xc8ced6,
      drag: 1.5, rotSpeedDeg: 45,
      now: nowMs,
    });
  }

  private spawnSquadDeath(dead: Squad, nowMs: number): void {
    // Faction-tinted energy splatter decal + spark burst + smoke. The decal
    // is persistent (FIFO recycled) so the floor accumulates evidence of
    // skirmishes — making the arena feel like a chaotic warzone over time.
    const decalFrame = dead.ownerId === 1 ? 'blood_p1' : 'blood_p0';
    if (this.scene.textures.exists(TEX_POLISH)) {
      const decal = this.scene.add.image(dead.pos.x, dead.pos.y, TEX_POLISH, decalFrame);
      decal.setOrigin(0.5, 0.5);
      decal.setDepth(-100);   // above floor tile, below sprites
      decal.setDisplaySize(80, 52);
      decal.setAlpha(0.9);
      decal.setRotation(Math.random() * Math.PI * 2);
      this.pushDecal(this.bloodDecals, MAX_BLOOD_DECALS, decal);
    }
    if (!this.scene.textures.exists(TEX_FX)) return;
    const tint = dead.ownerId === 1 ? 0xff9670 : 0x7fb8ff;
    // Large primary energy burst
    this.spawn({
      tex: TEX_FX, frame: 'hit_large',
      x: dead.pos.x, y: dead.pos.y, vx: 0, vy: 0,
      durMs: 280, restMs: 0,
      scaleFrom: 1.1, scaleTo: 2.0,
      alphaFrom: 1, alphaTo: 0,
      angleDeg: Math.random() * 360,
      depth: 14, blend: Phaser.BlendModes.ADD,
      tint,
      drag: 0, rotSpeedDeg: 0,
      now: nowMs,
    });
    // Secondary smaller burst slightly offset (delayed shimmer)
    this.spawn({
      tex: TEX_FX, frame: 'hit_small',
      x: dead.pos.x + (Math.random() - 0.5) * 10,
      y: dead.pos.y + (Math.random() - 0.5) * 10, vx: 0, vy: 0,
      durMs: 180, restMs: 0,
      scaleFrom: 0.8, scaleTo: 1.5,
      alphaFrom: 0.9, alphaTo: 0,
      angleDeg: Math.random() * 360,
      depth: 13.8, blend: Phaser.BlendModes.ADD,
      tint: 0xffffff,
      drag: 0, rotSpeedDeg: 0,
      now: nowMs,
    });
    // Dense radial spark burst (12 sparks for more punch)
    const count = 12;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + Math.random() * 0.35;
      const speed = 100 + Math.random() * 180;
      this.spawn({
        tex: TEX_FX, frame: 'spark',
        x: dead.pos.x, y: dead.pos.y,
        vx: Math.cos(a) * speed, vy: Math.sin(a) * speed,
        durMs: 380 + Math.random() * 100, restMs: 0,
        scaleFrom: 1.1, scaleTo: 0.15,
        alphaFrom: 1, alphaTo: 0,
        angleDeg: 0,
        depth: 13.5, blend: Phaser.BlendModes.ADD,
        tint,
        drag: 2.8, rotSpeedDeg: 0,
        now: nowMs,
      });
    }
    // Smoke cloud
    this.spawn({
      tex: TEX_FX, frame: 'smoke_puff',
      x: dead.pos.x, y: dead.pos.y, vx: 0, vy: -12,
      durMs: 560, restMs: 0,
      scaleFrom: 0.55, scaleTo: 1.6,
      alphaFrom: 0.65, alphaTo: 0,
      angleDeg: 0,
      depth: 12.5, blend: Phaser.BlendModes.NORMAL,
      tint: 0xc0c8d0,
      drag: 0.6, rotSpeedDeg: 28,
      now: nowMs,
    });
  }

  private spawnExplosion(x: number, y: number, nowMs: number, sizeMul: number): void {
    // Persistent scorch decal
    if (this.scene.textures.exists(TEX_POLISH)) {
      const decal = this.scene.add.image(x, y, TEX_POLISH, 'scorch_decal');
      decal.setOrigin(0.5, 0.5);
      decal.setDepth(-90);
      decal.setDisplaySize(120 * sizeMul, 76 * sizeMul);
      decal.setAlpha(0.88);
      decal.setRotation(Math.random() * Math.PI * 2);
      this.pushDecal(this.scorchDecals, MAX_SCORCH_DECALS, decal);
    }
    if (!this.scene.textures.exists(TEX_FX)) return;
    // White-hot primary core flash
    this.spawn({
      tex: TEX_FX, frame: 'explosion_core',
      x, y, vx: 0, vy: 0,
      durMs: EXPLOSION_CORE_MS, restMs: 0,
      scaleFrom: 0.7 * sizeMul, scaleTo: 1.9 * sizeMul,
      alphaFrom: 1, alphaTo: 0,
      angleDeg: Math.random() * 360,
      depth: 14.5, blend: Phaser.BlendModes.ADD,
      tint: 0xffffff,
      drag: 0, rotSpeedDeg: 0,
      now: nowMs,
    });
    // Secondary delayed fireball (orange, slightly larger, slower fade)
    this.spawn({
      tex: TEX_FX, frame: 'explosion_core',
      x, y, vx: 0, vy: 0,
      durMs: EXPLOSION_CORE2_MS, restMs: 0,
      scaleFrom: 0.55 * sizeMul, scaleTo: 2.2 * sizeMul,
      alphaFrom: 0.85, alphaTo: 0,
      angleDeg: Math.random() * 360,
      depth: 14, blend: Phaser.BlendModes.ADD,
      tint: 0xff6820,
      drag: 0, rotSpeedDeg: 0,
      now: nowMs,
    });
    // Expanding primary shockwave ring
    this.spawn({
      tex: TEX_FX, frame: 'explosion_ring',
      x, y, vx: 0, vy: 0,
      durMs: EXPLOSION_RING_MS, restMs: 0,
      scaleFrom: 0.5 * sizeMul, scaleTo: 2.8 * sizeMul,
      alphaFrom: 1, alphaTo: 0,
      angleDeg: 0,
      depth: 13.8, blend: Phaser.BlendModes.ADD,
      tint: 0xffb060,
      drag: 0, rotSpeedDeg: 0,
      now: nowMs,
    });
    // Second wider slower ring (outer pressure wave)
    this.spawn({
      tex: TEX_FX, frame: 'explosion_ring',
      x, y, vx: 0, vy: 0,
      durMs: EXPLOSION_RING_MS * 1.3, restMs: 0,
      scaleFrom: 0.3 * sizeMul, scaleTo: 3.6 * sizeMul,
      alphaFrom: 0.5, alphaTo: 0,
      angleDeg: 0,
      depth: 13.5, blend: Phaser.BlendModes.ADD,
      tint: 0xff7020,
      drag: 0, rotSpeedDeg: 0,
      now: nowMs,
    });
    // Dense radial debris sparks
    const sparkCount = Math.round(16 * sizeMul);
    for (let i = 0; i < sparkCount; i++) {
      const a = (i / sparkCount) * Math.PI * 2 + Math.random() * 0.35;
      const speed = 200 + Math.random() * 240;
      this.spawn({
        tex: TEX_FX, frame: 'spark',
        x, y,
        vx: Math.cos(a) * speed, vy: Math.sin(a) * speed,
        durMs: 560 + Math.random() * 220, restMs: 0,
        scaleFrom: 1.4, scaleTo: 0.15,
        alphaFrom: 1, alphaTo: 0,
        angleDeg: 0,
        depth: 13.2, blend: Phaser.BlendModes.ADD,
        tint: 0xffd070,
        drag: 1.8, rotSpeedDeg: 0,
        now: nowMs,
      });
    }
    // Smoke cloud (two layers for depth)
    this.spawn({
      tex: TEX_FX, frame: 'smoke_puff',
      x, y, vx: 0, vy: -15,
      durMs: 900, restMs: 0,
      scaleFrom: 0.5 * sizeMul, scaleTo: 1.6 * sizeMul,
      alphaFrom: 0.7, alphaTo: 0,
      angleDeg: 0,
      depth: 12.5, blend: Phaser.BlendModes.NORMAL,
      tint: 0xa8b0b8,
      drag: 0.5, rotSpeedDeg: 8,
      now: nowMs,
    });
    // Dust ring (non-additive, ground-hugging)
    this.spawn({
      tex: TEX_POLISH, frame: 'explosion_dust',
      x, y, vx: 0, vy: 0,
      durMs: 900, restMs: 0,
      scaleFrom: 0.5 * sizeMul, scaleTo: 1.5 * sizeMul,
      alphaFrom: 0.7, alphaTo: 0,
      angleDeg: 0,
      depth: -50, blend: Phaser.BlendModes.NORMAL,
      tint: 0xb8c0c8,
      drag: 0, rotSpeedDeg: 6,
      now: nowMs,
    });
  }

  // V1.5+ — relay flip shockwave. EMP-style ring + white pop + 10 sparks
  // tinted with the new owner's color. Sells the §16 "felt on the
  // battlefield" promise. Pure visual.
  private spawnFlipShockwave(x: number, y: number, tint: number, nowMs: number): void {
    if (!this.scene.textures.exists(TEX_FX)) return;
    this.spawn({
      tex: TEX_FX, frame: 'explosion_ring', x, y, vx: 0, vy: 0,
      durMs: 700, restMs: 0, scaleFrom: 0.4, scaleTo: 3.4,
      alphaFrom: 1, alphaTo: 0, angleDeg: 0, depth: 13.7,
      blend: Phaser.BlendModes.ADD, tint, drag: 0, rotSpeedDeg: 0, now: nowMs,
    });
    this.spawn({
      tex: TEX_FX, frame: 'hit_large', x, y, vx: 0, vy: 0,
      durMs: 240, restMs: 0, scaleFrom: 0.7, scaleTo: 1.8,
      alphaFrom: 1, alphaTo: 0, angleDeg: Math.random() * 360, depth: 14,
      blend: Phaser.BlendModes.ADD, tint: 0xffffff, drag: 0, rotSpeedDeg: 0, now: nowMs,
    });
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2 + Math.random() * 0.2;
      const speed = 180 + Math.random() * 120;
      this.spawn({
        tex: TEX_FX, frame: 'spark', x, y,
        vx: Math.cos(a) * speed, vy: Math.sin(a) * speed,
        durMs: 420 + Math.random() * 120, restMs: 0,
        scaleFrom: 1.1, scaleTo: 0.2, alphaFrom: 1, alphaTo: 0,
        angleDeg: 0, depth: 13.5, blend: Phaser.BlendModes.ADD,
        tint, drag: 2.2, rotSpeedDeg: 0, now: nowMs,
      });
    }
  }

  // -------------------------------------------------------------------------
  // Particle pool primitives
  // -------------------------------------------------------------------------

  private spawn(opts: {
    tex: string; frame: string;
    x: number; y: number; vx: number; vy: number;
    durMs: number; restMs: number;
    scaleFrom: number; scaleTo: number;
    alphaFrom: number; alphaTo: number;
    angleDeg: number;
    depth: number; blend: Phaser.BlendModes;
    tint: number;
    drag: number; rotSpeedDeg: number;
    now: number;
  }): void {
    let img = this.pool.pop();
    if (img === undefined) {
      img = this.scene.add.image(opts.x, opts.y, opts.tex, opts.frame);
    } else {
      img.setTexture(opts.tex, opts.frame);
      img.setVisible(true);
      img.setActive(true);
      img.setPosition(opts.x, opts.y);
    }
    img.setOrigin(0.5, 0.5);
    img.setDepth(opts.depth);
    img.setBlendMode(opts.blend);
    img.setAngle(opts.angleDeg);
    img.setScale(opts.scaleFrom);
    img.setAlpha(opts.alphaFrom);
    img.setTint(opts.tint);
    this.active.push({
      img,
      bornMs: opts.now,
      durMs: opts.durMs,
      restMs: opts.restMs,
      x: opts.x, y: opts.y,
      vx: opts.vx, vy: opts.vy,
      scaleFrom: opts.scaleFrom, scaleTo: opts.scaleTo,
      alphaFrom: opts.alphaFrom, alphaTo: opts.alphaTo,
      drag: opts.drag, rotSpeedDeg: opts.rotSpeedDeg,
    });
  }

  private recycle(p: Particle): void {
    p.img.setVisible(false);
    p.img.setActive(false);
    p.img.clearTint();
    p.img.setBlendMode(Phaser.BlendModes.NORMAL);
    this.pool.push(p.img);
  }

  private pushDecal(list: Phaser.GameObjects.Image[], cap: number, decal: Phaser.GameObjects.Image): void {
    list.push(decal);
    while (list.length > cap) {
      const old = list.shift();
      old?.destroy();
    }
  }
}
